import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import classNames from "classnames";
import { useParams } from "react-router-dom";
import BaseModal from "@app/components/modal";
import PdfViewerModal from "@app/components/pdf-viewer/modal";
import ExcelViewerModal from "@app/components/excel-viewer/modal";
import Loader from "@app/components/loader/content";
import { events } from "@app/lib/store";
import { Drawer } from "antd";
import notify from "@app/components/notify";
import http from "@app/lib/http";
import Header from "./header";
import ChatList from "./chat-list";
import Conversation from "./conversation";
import { ShrinkedChatList } from "./shrinked-chat-list";
import { calculateAreaCoordinates, referenceLabel } from "./helper";

import chatStore from "../../state/store/ai-chat";
import bookmarksStore from "../../state/store/report/document/bookmarks";

import "./style/modal.global.scss";
import "./style/modal.scoped.scss";

const Modal = observer(({ container }) => {
    const { project } = useParams();
    const [showRef, setShowRef] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [isResizingRight, setIsResizingRight] = useState(false);
    const resizeRef = useRef({ startX: 0, startWidth: 0 });
    const resizeRightRef = useRef({ startX: 0, startWidth: 0, startRight: 0 });

    /**
     * Handle the dialog close event
     */
    const onCancel = () => {
        chatStore.hide();
    };

    const onToggleExpand = (expanded) => {
        chatStore.expanded = expanded;
        setDrawerVisible(false);
    };

    const onCreateChat = () => {
        chatStore.newChat();
        setDrawerVisible(false);
    };

    const onPrompt = (prompt) => {
        chatStore.prompt(prompt);
    };

    const onRenameChat = (chatId, name) => {
        chatStore.renameChat(chatId, name);
    };

    const onRemoveChat = (chatId) => {
        chatStore.removeChat(chatId);
    };

    const onOpenChat = (chatId) => {
        chatStore.loadChat(chatId);
        setDrawerVisible(false);
    };

    const toggleDrawerVisibile = () => {
        setDrawerVisible(!drawerVisible);
    };

    const handleDrawerClose = () => {
        setDrawerVisible(false);
    };

    const onThumbsUp = (answerId) => {
        chatStore.thumbsUp(answerId);
    };

    const onThumbsDown = (answerId) => {
        chatStore.thumbsDown(answerId);
    };

    const onCancelPrompt = () => {
        chatStore.cancelPrompt();
    };

    const onEditMessage = (question) => {
        chatStore.setDraftQuestion(question);
    };

    const hideRefModal = () => {
        setShowRef(null);
    };

    const showRefModal = (ref) => {
        if (ref.documentType === "excel") {
            setShowRef({
                type: "excel",
                file: ref.fileId,
                name: ref.fileName,
                sheet: ref.sheet,
                documentId: ref.documentId ?? null,
            });
            return;
        }

        const points = ref.coordinates.points;
        const width = ref.coordinates.layout_width;
        const height = ref.coordinates.layout_height;

        const coordinates = calculateAreaCoordinates(points, width, height);

        const area = {
            pageIndex: ref.page - 1,
            ...coordinates,
        };

        setShowRef({
            type: "pdf",
            file: ref.fileId,
            name: referenceLabel(ref),
            area,
            documentId: ref.documentId ?? null,
            articleId: ref.articleId ?? null,
            stage: ref.stage ?? null,
            articleType: ref.articleType ?? null,
        });
    };

    const onBookmark = async (bookmark) => {
        if (!showRef?.documentId) {
            return;
        }
        try {
            await bookmarksStore.save({ ...bookmark, document: showRef.documentId });
        } catch {
            notify.error("Error saving the bookmark");
        }
    };

    const onComment = async (comment) => {
        if (!showRef?.documentId) {
            return;
        }
        try {
            await http.post(`/project/${project}/document/${showRef.documentId}/comment`, comment);
            notify.success("Comment added");
        } catch {
            notify.error("Error saving the comment");
        }
    };

    const onNavigationChanged = () => {
        chatStore.expanded = false;
        hideRefModal();
    };

    const onResizeStart = (e) => {
        resizeRef.current = { startX: e.clientX, startWidth: chatStore.collapsedWidth };
        setIsResizing(true);
        e.preventDefault();
        e.stopPropagation();
    };

    const onRightResizeStart = (e) => {
        resizeRightRef.current = {
            startX: e.clientX,
            startWidth: chatStore.collapsedWidth,
            startRight: chatStore.collapsedRight,
        };
        setIsResizingRight(true);
        e.preventDefault();
        e.stopPropagation();
    };

    useEffect(() => {
        events.on("navigation.navigate", onNavigationChanged);

        return () => {
            events.off("navigation.navigate", onNavigationChanged);
        };
    });

    useEffect(() => {
        const active = isResizing || isResizingRight;
        document.body.style.userSelect = active ? "none" : "";
        document.body.style.cursor = active ? "ew-resize" : "";

        return () => {
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
        };
    }, [isResizing, isResizingRight]);

    useEffect(() => {
        if (!isResizing) {
            return;
        }

        const onMouseMove = (e) => {
            // Modal is anchored to the right, so dragging left widens it
            const delta = resizeRef.current.startX - e.clientX;
            const containerWidth = container ? container.clientWidth : window.innerWidth;
            const maxWidth = Math.floor(containerWidth / 1.5);
            const newWidth = Math.max(
                300,
                Math.min(maxWidth, resizeRef.current.startWidth + delta),
            );
            chatStore.setCollapsedWidth(newWidth);
        };

        const onMouseUp = () => setIsResizing(false);

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);

        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, [isResizing]);

    useEffect(() => {
        if (!isResizingRight) {
            return;
        }

        const onMouseMove = (e) => {
            // Right edge: dragging right shrinks the right offset, left edge stays fixed
            const { startX, startWidth, startRight } = resizeRightRef.current;
            const containerWidth = container ? container.clientWidth : window.innerWidth;
            const maxWidth = Math.max(startWidth, Math.floor(containerWidth / 1.5));
            // Clamp delta itself so width and right offset move together and the pane never slides
            const delta = Math.min(
                Math.max(e.clientX - startX, 300 - startWidth),
                startRight,
                maxWidth - startWidth,
            );
            chatStore.setCollapsedWidth(startWidth + delta);
            chatStore.setCollapsedRight(startRight - delta);
        };

        const onMouseUp = () => setIsResizingRight(false);

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);

        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, [isResizingRight]);

    return (
        <>
            {chatStore.opened && (
                <BaseModal
                    title={
                        <Header
                            expanded={chatStore.expanded}
                            onToggle={onToggleExpand}
                            onCreate={onCreateChat}
                            onDrawerClick={toggleDrawerVisibile}
                            collapsedWidth={chatStore.collapsedWidth}
                        />
                    }
                    visible={true}
                    onCancel={onCancel}
                    footer={null}
                    width={!chatStore.expanded ? chatStore.collapsedWidth : undefined}
                    style={!chatStore.expanded ? { right: chatStore.collapsedRight } : undefined}
                    height="50vh"
                    maskClosable={false}
                    mask={false}
                    className={classNames("global-ai-chat", { expanded: chatStore.expanded })}
                    wrapClassName="global-ai-chat-wrap"
                    getContainer={() => container}
                >
                    {!chatStore.expanded && (
                        <>
                            <div className="resize-handle" onMouseDown={onResizeStart} />
                            <div className="resize-handle-right" onMouseDown={onRightResizeStart} />
                        </>
                    )}
                    {chatStore.chatsLoaded && (
                        <>
                            <ChatList
                                chats={chatStore.chats}
                                activeChat={chatStore.activeChat}
                                onOpen={onOpenChat}
                                onRename={onRenameChat}
                                onRemove={onRemoveChat}
                            />
                            <Conversation
                                messages={chatStore.messages}
                                activeChat={chatStore.activeChat}
                                loading={chatStore.loadingChat}
                                busy={chatStore.busy}
                                onPrompt={onPrompt}
                                onShowRef={showRefModal}
                                onThumbsUp={onThumbsUp}
                                onThumbsDown={onThumbsDown}
                                onCancel={onCancelPrompt}
                                onEditMessage={onEditMessage}
                            />
                        </>
                    )}

                    {!chatStore.chatsLoaded && (
                        <div className="loading-chat">
                            <Loader loading={true} />
                            <em>Loading...</em>
                        </div>
                    )}

                    {showRef?.type === "pdf" && (
                        <PdfViewerModal
                            container={container}
                            onClose={hideRefModal}
                            fileId={showRef.file}
                            title={showRef.name}
                            focus={true}
                            goto={showRef.area}
                            documentId={showRef.documentId}
                            articleId={showRef.articleId}
                            stage={showRef.stage}
                            articleType={showRef.articleType}
                            onBookmark={showRef.documentId ? onBookmark : undefined}
                            onComment={showRef.documentId ? onComment : undefined}
                        ></PdfViewerModal>
                    )}
                    {showRef?.type === "excel" && (
                        <ExcelViewerModal
                            container={container}
                            onClose={hideRefModal}
                            fileId={showRef.file}
                            title={showRef.name}
                            sheet={showRef.sheet}
                            onBookmark={showRef.documentId ? onBookmark : undefined}
                            onComment={showRef.documentId ? onComment : undefined}
                        />
                    )}
                    <div className="previous-chat-drawer">
                        <Drawer
                            title="Recent Chats"
                            placement="left"
                            onClose={handleDrawerClose}
                            visible={drawerVisible}
                            width={300}
                            height="100%"
                            getContainer={false}
                            style={{ position: "absolute" }}
                            key={"left"}
                        >
                            {chatStore.chatsLoaded && (
                                <ShrinkedChatList
                                    chats={chatStore.chats}
                                    activeChat={chatStore.activeChat}
                                    onOpen={onOpenChat}
                                />
                            )}
                        </Drawer>
                    </div>
                </BaseModal>
            )}
        </>
    );
});

export default Modal;
